import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const VisaApplicationDetailPage = () => {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [forms, setForms] = useState([]);
  const [fees, setFees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [controlStatus, setControlStatus] = useState(null);
  const showToast = (type, text) => { setToast({ type, text }); window.clearTimeout(showToast._t); showToast._t = window.setTimeout(() => setToast(null), 2500); };

  const load = async () => {
    setLoading(true);
    try {
      const { data: a } = await supabase.from('visa_applications').select('*, clients!inner(first_name,last_name), visa_products!inner(country,visa_type,name)').eq('id', id).single();
      setApp(a || null);

      if (a) {
        const { data: ms } = await supabase.from('visa_milestones').select('*').eq('visa_application_id', id).order('started_at', { ascending: true });
        setMilestones(ms || []);
        const { data: fm } = await supabase.from('visa_forms').select('*').eq('visa_application_id', id).order('updated_at', { ascending: false });
        setForms(fm || []);
        const { data: fp } = await supabase.from('visa_fee_payments').select('*').eq('visa_application_id', id).order('paid_at', { ascending: false });
        setFees(fp || []);

        // Load documents if submission_id exists
        if (a.submission_id) {
          const { data: docs } = await supabase.from('submission_documents')
            .select('*')
            .eq('submission_id', a.submission_id)
            .order('created_at', { ascending: false });
          setDocuments(docs || []);
        }
      }

      // Control Room status (deadline awareness)
      const { data: deadlineRow } = await supabase
        .from('unified_deadlines')
        .select('deadline, deadline_type')
        .eq('entity_id', id)
        .eq('deadline_type', 'visa_application')
        .single();
      if (deadlineRow?.deadline) {
        const deadlineDate = new Date(deadlineRow.deadline);
        const now = new Date();
        const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
        let level = 'attention';
        if (deadlineDate < now || diffDays <= 0) level = 'critical';
        else if (diffDays <= 3) level = 'urgent';
        setControlStatus({
          level,
          deadline: deadlineDate,
          days: diffDays
        });
      } else {
        setControlStatus(null);
      }
    } catch (err) {
      console.error('Error loading visa app', err);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const uploadVisaDoc = async (file) => {
    if (!app?.submission_id || !file) return;
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) { showToast('error', 'File too large (max 10MB)'); return; }

    try {
      setUploading(true);
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `submissions/${app.submission_id}/visa/${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage.from('documents').upload(path, file);
      if (upErr) throw upErr;

      const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);

      const { error: insErr } = await supabase.from('submission_documents').insert([{
        submission_id: app.submission_id,
        title: file.name,
        file_url: pub.publicUrl,
        file_path: path,
        status: 'uploaded',
        uploaded_at: new Date().toISOString(),
        is_internal: false
      }]);

      if (insErr) throw insErr;

      showToast('success', 'Document uploaded');
      load();
    } catch (e) {
      console.error(e);
      showToast('error', 'Upload failed');
    } finally {
      setUploading(false);
    }
  };


  const [newMilestone, setNewMilestone] = useState({ stage_key: '', title: '' });
  const addMilestone = async () => {
    if (!newMilestone.stage_key.trim() || !newMilestone.title.trim()) return showToast('error', 'Stage and title required');
    const { error } = await supabase.from('visa_milestones').insert([{ visa_application_id: id, stage_key: newMilestone.stage_key, title: newMilestone.title, status: 'pending' }]);
    if (error) showToast('error', 'Failed to add milestone'); else { showToast('success', 'Milestone added'); setNewMilestone({ stage_key: '', title: '' }); load(); }
  };

  const updateMilestoneStatus = async (msId, status) => {
    const payload = { status };
    if (status === 'pending') {
      payload.started_at = null;
      payload.completed_at = null;
    }
    if (status === 'active') {
      payload.started_at = new Date().toISOString();
    }
    if (status === 'done') {
      payload.completed_at = new Date().toISOString();
    }
    const { error } = await supabase.from('visa_milestones').update(payload).eq('id', msId);
    if (error) showToast('error', 'Failed to update milestone'); else { showToast('success', 'Milestone updated'); load(); }
  };

  const [newForm, setNewForm] = useState({ form_key: '', reference_no: '' });
  const addForm = async () => {
    if (!newForm.form_key.trim()) return showToast('error', 'Form key required');
    const { error } = await supabase.from('visa_forms').insert([{ visa_application_id: id, form_key: newForm.form_key, reference_no: newForm.reference_no || null, status: 'draft' }]);
    if (error) showToast('error', 'Failed to add form'); else { showToast('success', 'Form added'); setNewForm({ form_key: '', reference_no: '' }); load(); }
  };

  const [newFee, setNewFee] = useState({ amount: '', currency: 'USD', reference: '' });
  const addFee = async () => {
    if (!newFee.amount.trim()) return showToast('error', 'Amount required');
    const payload = { visa_application_id: id, amount: Number(newFee.amount), currency: newFee.currency, paid_at: new Date().toISOString(), reference: newFee.reference || null };
    const { error } = await supabase.from('visa_fee_payments').insert([payload]);
    if (error) showToast('error', 'Failed to record fee'); else { showToast('success', 'Fee recorded'); setNewFee({ amount: '', currency: 'USD', reference: '' }); load(); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <div>Loading visa application...</div>
    </div>
  );

  if (!app) return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4 text-gray-500">🛂</div>
      <div>Visa application not found.</div>
    </div>
  );

  const clientName = `${app.clients?.first_name || ''} ${app.clients?.last_name || ''}`.trim();
  const productLabel = app.visa_products ? `${app.visa_products.country} • ${app.visa_products.visa_type || ''} • ${app.visa_products.name || ''}` : '-';

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Toast */}
      {toast && (
        <div className={`p-4 rounded border mb-6 ${toast.type === 'success' ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-red-900/20 border-red-800 text-red-400'}`}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">🛂 Visa Application Details</h1>
        <p className="text-gray-400">Manage visa application milestones, forms, and fees</p>
        {controlStatus && (
          <div className="text-xs text-gray-400 mt-2 flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded border ${controlStatus.level === 'critical' ? 'bg-red-900/30 text-red-300 border-red-800' :
              controlStatus.level === 'urgent' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' :
                'bg-blue-900/30 text-blue-300 border-blue-800'
              }`}>
              {controlStatus.level.toUpperCase()}
            </span>
            <span>
              {controlStatus.deadline ? controlStatus.deadline.toLocaleDateString() : 'No deadline'}
              {typeof controlStatus.days === 'number' ? ` (${controlStatus.days}d)` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Application Info */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">{clientName || '-'} — {productLabel}</h2>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            <span className={`px-2 py-1 rounded text-xs font-medium border ${app.status === 'approved' ? 'bg-green-900/20 text-green-400 border-green-800' : app.status === 'rejected' ? 'bg-red-900/20 text-red-400 border-red-800' : app.status === 'decision' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-800' : 'bg-blue-900/20 text-blue-400 border-blue-800'}`}>
              {app.status}
            </span>
            <span>Updated: {app.updated_at ? new Date(app.updated_at).toLocaleString() : '-'}</span>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Milestones */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">🎯 <span>Milestones</span></h4>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={newMilestone.stage_key}
                onChange={(e) => setNewMilestone({ ...newMilestone, stage_key: e.target.value })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Stage key (biometrics)"
              />
              <input
                value={newMilestone.title}
                onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Title"
              />
              <button onClick={addMilestone} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Add</button>
            </div>
            <ul className="space-y-2 mt-4">
              {milestones.map(m => (
                <li key={m.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-white">{m.title}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${m.status === 'done' ? 'bg-green-900/20 text-green-400 border-green-800' : m.status === 'active' ? 'bg-blue-900/20 text-blue-400 border-blue-800' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                          {m.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => updateMilestoneStatus(m.id, 'pending')} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">Pending</button>
                      <button onClick={() => updateMilestoneStatus(m.id, 'active')} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Active</button>
                      <button onClick={() => updateMilestoneStatus(m.id, 'done')} className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700">Done</button>
                    </div>
                  </div>
                </li>
              ))}
              {milestones.length === 0 && <li className="text-center py-8 text-gray-500">No milestones</li>}
            </ul>
          </div>
        </div>

        {/* Forms */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">📄 <span>Forms</span></h4>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={newForm.form_key}
                onChange={(e) => setNewForm({ ...newForm, form_key: e.target.value })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Form key (DS-160)"
              />
              <input
                value={newForm.reference_no}
                onChange={(e) => setNewForm({ ...newForm, reference_no: e.target.value })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Reference no"
              />
              <button onClick={addForm} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Add</button>
            </div>
            <ul className="space-y-2 mt-4">
              {forms.map(f => (
                <li key={f.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                  <div className="flex-1">
                    <div className="font-semibold text-white">{f.form_key}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      <span>Ref: {f.reference_no || '-'} — </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${f.status === 'completed' ? 'bg-green-900/20 text-green-400 border-green-800' : f.status === 'in_progress' ? 'bg-blue-900/20 text-blue-400 border-blue-800' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                        {f.status || '-'}
                      </span>
                    </div>
                  </div>


                </li>
              ))}
              {forms.length === 0 && <li className="text-center py-8 text-gray-500">No forms</li>}
            </ul>
          </div>
        </div>

        {/* Fees */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">💰 <span>Fees</span></h4>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={newFee.amount}
                onChange={(e) => setNewFee({ ...newFee, amount: e.target.value.replace(/[^0-9.]/g, '') })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Amount"
              />
              <input
                value={newFee.currency}
                onChange={(e) => setNewFee({ ...newFee, currency: e.target.value })}
                className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Currency"
              />
              <button onClick={addFee} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Add</button>
            </div>
            <input
              value={newFee.reference}
              onChange={(e) => setNewFee({ ...newFee, reference: e.target.value })}
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Reference"
            />
            <ul className="space-y-2 mt-4">
              {fees.map(p => (
                <li key={p.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                  <div className="flex-1">
                    <div className="font-semibold text-white">{p.amount} {p.currency}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Ref: {p.reference || '-'} — Paid: {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}
                    </div>
                  </div>
                </li>
              ))}
              {fees.length === 0 && <li className="text-center py-8 text-gray-500">No fee payments</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">📂 <span>Documents</span></h4>
            <div className="relative overflow-hidden">
              <button
                disabled={uploading || !app.submission_id}
                className={`px-3 py-1.5 rounded text-sm font-medium ${!app.submission_id ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              {app.submission_id && !uploading && (
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => uploadVisaDoc(e.target.files[0])}
                />
              )}
            </div>
          </div>

          {!app.submission_id && (
            <div className="text-xs text-yellow-500 mb-3 bg-yellow-900/20 p-2 rounded border border-yellow-800">
              No linked submission. Cannot upload documents.
            </div>
          )}

          <ul className="space-y-2">
            {documents.map(d => (
              <li key={d.id} className="p-3 bg-gray-700/50 rounded border border-gray-600 flex justify-between items-center">
                <div className="overflow-hidden">
                  <div className="font-semibold text-white truncate" title={d.title}>{d.title}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : '-'}
                  </div>
                </div>
                {d.file_url && (
                  <a
                    href={d.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 ml-2 flex-shrink-0"
                    title="View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  </a>
                )}
              </li>
            ))}
            {documents.length === 0 && <li className="text-center py-8 text-gray-500">No documents</li>}
          </ul>
        </div>
      </div>
    </div>

  );
};

export default VisaApplicationDetailPage;
